import styled from 'styled-components'
import {
    KeyboardEvent,
    useContext,
    useEffect,
    useRef,
    useState,
    useMemo,
} from 'react'
import {AuthContext} from '../../../../../contexts/auth'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import {AssistantApi} from '../../../../../services/AssistantApi.service'
import {HistoryContext} from '../../../../../contexts/history'
import {useLoading} from '../../../../../globalComponents/useLoading'
import TextareaAutosize from '@mui/material/TextareaAutosize'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import AddIcon from '@mui/icons-material/Add'
import ChatCounter from './charCounter'
import {
    List,
    ListItem as MUIListItem,
    ListItemText,
    Box,
    Alert,
    CircularProgress,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {uploadFilesToS3} from '../../../../../globalComponents/uploadFile'
import StopSharpIcon from '@mui/icons-material/StopSharp'
import pdf from '../../../../../assets/pdf.png'
import docx from '../../../../../assets/docx.png'
import {getCurrentColors} from '../../../../../utils/colors'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'

interface MotorButtonProps {
    isOpen: boolean
}

interface DropdownMenuProps {
    isOpen: boolean
}

interface MenuItemProps {
    isSelected: boolean
}

const COLORS = getCurrentColors()

const InputWrapper = styled.div`
    width: 795px;
    max-width: 100%;
    background: white;
    border-radius: 15px;
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.14);
`

const InputContainer = styled.div`
    position: relative;
    padding: 0 12px 12px;
    z-index: 5;
`

const InputRow = styled.div`
    grid-area: input;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    width: 100%;
    position: relative;
`

const TextareaContainer = styled.div`
    flex: 1;
    min-width: 0;
    max-height: 230px;
    margin-right: 8px;
    overflow-y: auto;
`

const StyledTextarea = styled(TextareaAutosize)`
    width: 100%;
    min-height: 40px;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.5;
    padding: 8px;
    color: #000;
    background: transparent;
    border: none;
    resize: none;
    outline: none;
    margin-bottom: 4px;

    &::placeholder {
        color: #000000;
    }
`

const MotorButton = styled.button<MotorButtonProps>`
    display: flex;
    align-items: center;
    gap: 4px;
    background: #f3f4f6;
    padding: 6px 10px;
    border-radius: 20px;
    font-size: 13px;
    color: #4b5563;
    border: none;
    cursor: pointer;
    transition: all 0.2s;
    min-width: 180px;
    height: 32px;
    white-space: nowrap;
    justify-content: space-between;
    flex-shrink: 0;

    &:hover {
        background: #e5e7eb;
    }

    .button-content {
        display: flex;
        align-items: center;
        gap: 6px;
    }

    .arrow-icon {
        font-size: 16px;
        transition: transform 0.2s;
        transform: ${({isOpen}) => (isOpen ? 'rotate(180deg)' : 'rotate(0)')};
    }
`

const DropdownMenu = styled.div<DropdownMenuProps>`
    position: absolute;
    top: 38px;
    right: 0;
    background: white;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
    display: ${({isOpen}) => (isOpen ? 'block' : 'none')};
    z-index: 10;
    min-width: 180px;
`

const MenuItem = styled.button<MenuItemProps>`
    width: 100%;
    padding: 8px 16px;
    text-align: left;
    background: ${({isSelected}) => (isSelected ? '#f3f4f6' : 'transparent')};
    border: none;
    cursor: pointer;
    font-size: 14px;
    color: #4b5563;
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover {
        background: #f3f4f6;
    }

    &:first-child {
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
    }

    &:last-child {
        border-bottom-left-radius: 8px;
        border-bottom-right-radius: 8px;
    }
`

const ComposerLayout = styled.div<{isExpanded: boolean}>`
    display: grid;
    gap: 8px;
    align-items: center;
    margin-top: 4px;

    grid-template-columns: ${({isExpanded}) =>
        isExpanded ? '1fr 1fr auto' : 'auto 1fr auto'};
    grid-template-areas: ${({isExpanded}) =>
        isExpanded
            ? '"input input input" "left left send"'
            : '"left input send"'};
`

const LeftButtons = styled.div`
    grid-area: left;
    display: flex;
    gap: 16px;
`

const FileDropdownWrapper = styled.div`
    position: relative;
    display: flex;
`

const AddFileDropdownTrigger = styled.button`
    width: 40px;
    height: 40px;
    background: transparent;
    color: ${COLORS.botaoFormulario};
    border: 1px solid ${COLORS.botaoFormulario};
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: rgba(3, 71, 128, 0.08);
    }

    &:disabled {
        background: transparent;
        color: #9ca3af;
        border-color: #d1d5db;
        cursor: not-allowed;
    }
`

const AddFileDropdownMenu = styled.div<DropdownMenuProps>`
    position: absolute;
    bottom: calc(100% + 8px);
    left: 0;
    background: white;
    border-radius: 10px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
    display: ${({isOpen}) => (isOpen ? 'block' : 'none')};
    z-index: 15;
    min-width: 180px;
`

const AddFileDropdownItem = styled.button`
    width: 100%;
    padding: 8px 12px;
    text-align: left;
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: 14px;
    color: #374151;
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover {
        background: #f3f4f6;
    }

    &:disabled {
        cursor: not-allowed;
        color: #9ca3af;
    }
`

const IconButton = styled.button`
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;

    border: none;
    padding: 4px;
    cursor: pointer;
    color: #292d32;
    transition: color 0.2s;

    &:hover {
        background: transparent;
        color: #6b7280;
    }

    svg {
        width: 20px;
        height: 20px;
    }
`

const SendButtonContainer = styled.div`
    grid-area: send;
    justify-self: end;
    position: relative;
    display: inline-block;
`

const SendButton = styled.button`
    width: 40px;
    background: ${COLORS.botaoFormulario};
    color: white;
    border: none;
    padding: 6px 12px;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: rgb(3, 71, 128);
    }

    &:disabled {
        background: #9ca3af;
        cursor: not-allowed;
    }
`

const StyledChatCounter = styled.div`
    position: absolute;
    top: 20%;
    left: 170%;
    transform: translateX(-50%);
    margin-top: 4px;
    color: #9ca3af;

    .counter {
        font-size: 10px;
        color: #9ca3af;
        white-space: nowrap;
    }

    span {
        color: #9ca3af !important;
    }
`

const ButtonTitle = styled.div`
    @media (max-width: 455px) {
        text-align: left;
        font-size: 10px;
        white-space: normal;
        overflow-wrap: break-word;
        word-break: break-word;
        line-height: 1.2;
        max-width: 120px;   
    }
`

export default function Input({suggestion = ''}: {suggestion?: string}) {
    const MESSAGE_TIMEOUT_CHECK_INTERVAL_MS = 60000
    const MESSAGE_RESPONSE_TIMEOUT_MS = 300000
    const MESSAGE_EXPANSION_THRESHOLD_CHARS = 90
    const MAX_FILES = 3
    const MAX_FILES_PER_THREAD = 10
    const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024
    const MAX_PAGES_PER_THREAD = 600
    const {userInfo, logout} = useContext(AuthContext)
    const inputRef = useRef<HTMLTextAreaElement | null>(null)
    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const [newMessage, setNewMessage] = useState(suggestion)
    const [maxLength, setMaxLength] = useState<number>(1500)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const [isTextareaExpanded, setIsTextareaExpanded] = useState(false)
    const {LoadingMessage, setIsLoading, isLoading} = useLoading()
    const {
        selected,
        includeMessageAtArray,
        messagesArray,
        assistant,
        limits,
        setAssistant,
        assistants,
        setLoadingMessageBot,
        uuid,
        selectedFiles,
        setSelectedFiles,
        updateThreadUploadedDocuments,
    } = useContext(HistoryContext)
    const api = useMemo(() => new AssistantApi(), [])
    const assistantApi = useMemo(() => new AssistantApi(), [])
    const [showAlert, setShowAlert] = useState(false)
    const [showAlertInvalidExtension, setShowAlertInvalidExtension] =
        useState(false)
    const [showAlertFileSize, setShowAlertFileSize] = useState(false)
    const [showAlertCredits, setShowAlertCredits] = useState(false)
    const [showAlertThreadLimit, setShowAlertThreadLimit] = useState(false)
    const [showAlertPageLimit, setShowAlertPageLimit] = useState(false)
    const [showAlertFileNeedsText, setShowAlertFileNeedsText] =
        useState(false)
    const [uploadErrorMessage, setUploadErrorMessage] = useState('')
    const [loadedFiles, setLoadedFiles] = useState(true)
    const [isUploadingFiles, setIsUploadingFiles] = useState(false)
    const [removedFile, setRemovedFile] = useState(false)
    const [FilesIds, setFilesIds] = useState<string[]>([])
    const [totalInThread, setTotalInThread] = useState(
        selected?.uploaded_documents || 0
    )
    const [totalPagesInThread, setTotalPagesInThread] = useState(
        selected?.page_count || 0
    )

    useEffect(() => {
        setTotalInThread(selected?.uploaded_documents || 0)
        setTotalPagesInThread(selected?.page_count || 0)
        setShowAlert(false)
        setShowAlertInvalidExtension(false)
        setShowAlertFileSize(false)
        setShowAlertThreadLimit(false)
        setShowAlertPageLimit(false)
        setShowAlertFileNeedsText(false)
        setUploadErrorMessage('')
    }, [selected?.guid])

    useEffect(() => {
        console.log('totalInThread atualizado:', totalInThread)
        if (selected?.guid && selected.guid !== 'new') {
            updateThreadUploadedDocuments(selected.guid, totalInThread)
        }
    }, [totalInThread])

    const messagesArrayRef = useRef(messagesArray)
    const isLoadingRef = useRef(isLoading)

    useEffect(() => {
        messagesArrayRef.current = messagesArray
    }, [messagesArray])

    useEffect(() => {
        isLoadingRef.current = isLoading
    }, [isLoading])

    useEffect(() => {
        const intervalId = setInterval(() => {
            if (!isLoadingRef.current) {
                return
            }

            const latestMessages = messagesArrayRef.current
            const latestMessage = latestMessages[latestMessages.length - 1]

            if (!latestMessage?.author_id) {
                return
            }

            const createdAt = new Date(latestMessage.created_at).getTime()

            if (Number.isNaN(createdAt)) {
                return
            }

            const hasTimedOut =
                Date.now() - createdAt >= MESSAGE_RESPONSE_TIMEOUT_MS

            if (!hasTimedOut) {
                return
            }

            const threadId = latestMessage.thread_id || selected?.guid || uuid

            setIsLoading(false)
            setLoadingMessageBot(false)
            includeMessageAtArray({
                content:
                    'Não foi possível finalizar sua solicitação agora. Tente novamente.',
                thread_id: threadId,
                assistant_id: assistant?.guid || 'schief',
                author_id: '',
                created_at: new Date(),
                uploaded_documents: [],
            })
        }, MESSAGE_TIMEOUT_CHECK_INTERVAL_MS)

        return () => {
            clearInterval(intervalId)
        }
    }, [
        MESSAGE_TIMEOUT_CHECK_INTERVAL_MS,
        MESSAGE_RESPONSE_TIMEOUT_MS,
        assistant,
        includeMessageAtArray,
        selected,
        setIsLoading,
        setLoadingMessageBot,
        uuid,
    ])

    const getFileIcon = (fileName: string) => {
        const extension = fileName.split('.').pop()?.toLowerCase()

        if (extension === 'pdf') {
            return pdf
        }

        if (extension === 'docx') {
            return docx
        }

        return pdf
    }

    useEffect(() => {
        if (suggestion) {
            setNewMessage(suggestion)
        }
    }, [suggestion])

    useEffect(() => {
        setIsTextareaExpanded(
            newMessage.length >= MESSAGE_EXPANSION_THRESHOLD_CHARS
        )
    }, [MESSAGE_EXPANSION_THRESHOLD_CHARS, newMessage])

    useEffect(() => {
        setNewMessage('')
    }, [selected])

    useEffect(() => {
        setSelectedFiles([])
    }, [messagesArray])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                isDropdownOpen &&
                !(event.target as Element).closest('.motor-dropdown')
            ) {
                setIsDropdownOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [isDropdownOpen])

    useEffect(() => {
        async function fetchConstraints() {
            try {
                const maxLengthFromApi = await assistantApi.getConstraints()
                setMaxLength(maxLengthFromApi.max_characters)
            } catch (error) {
                console.error('Erro ao obter constraints:', error)
            }
        }
        fetchConstraints()
    }, [assistantApi])

    // useEffect(() => {
    //     if (selectedFiles.length === 0) {
    //         setIsUploadingDocuments(false)
    //     } else if (!removedFile) {
    //         setRemovedFile(true)
    //         handleFile(selectedFiles)
    //     }
    //     setTotalDocuments(selectedFiles.length)
    // }, [selectedFiles])

    useEffect(() => {
        const lastMessage = messagesArray[messagesArray.length - 1]
        if (lastMessage?.author_id) {
            setIsLoading(true)
            setLoadingMessageBot(true)
        } else {
            setIsLoading(false)
            setLoadingMessageBot(false)
        }
    }, [messagesArray, setIsLoading, setLoadingMessageBot])

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault()
            sendMessage()
        }
    }

    const handleSendMessage = async () => {
        if (
            isLoading ||
            (newMessage.trim().length === 0 && selected?.guid === 'new')
        )
            return

        if (selectedFiles.length > 0 && newMessage.trim().length === 0) {
            setShowAlertFileNeedsText(true)
            return
        }

        setIsLoading(true)
        let fileIds: string[] = []

        if (selectedFiles.length > 0) {
            try {
                setLoadedFiles(false)
                setIsUploadingFiles(true)
                setUploadErrorMessage('')
                fileIds = await uploadFilesToS3(
                    selectedFiles,
                    messagesArray[1]?.thread_id || uuid,
                    userInfo?.organization.guid || '',
                    userInfo?.guid || '',
                    assistant?.guid || ''
                )
                setFilesIds(fileIds)
                setLoadedFiles(true)
                setTotalInThread((prev) => prev + fileIds.length)
            } catch (error) {
                console.error('Erro durante o upload dos arquivos:', error)
                setUploadErrorMessage(
                    error instanceof Error
                        ? error.message
                        : 'Erro ao enviar arquivo. Tente novamente.'
                )
                setLoadedFiles(false)
                setIsLoading(false)
                return
            } finally {
                setIsUploadingFiles(false)
            }
        }
        sendMessage(fileIds)
        setSelectedFiles([])
    }

    function sendMessage(fileIds: string[] = []) {
        if (limits.usages.messages.current >= limits.usages.messages.max) {
            setShowAlertCredits(true)
            setIsLoading(false)
            setLoadingMessageBot(false)
            return
        }

        setIsLoading(true)
        setLoadingMessageBot(true)
        const thread_id = selected?.guid === 'new' ? uuid : selected?.guid

        if (
            !newMessage ||
            !thread_id ||
            !assistant ||
            newMessage.trim().length === 0
        ) {
            setIsLoading(false)
            setLoadingMessageBot(false)
            return
        }

        const messageToSend = newMessage

        includeMessageAtArray({
            assistant_id: assistant?.guid || '',
            created_at: new Date().toISOString(),
            thread_id: thread_id,
            author_id: 'user',
            content: messageToSend,
            uploaded_documents: selectedFiles.map((file) => file.name),
        })

        setNewMessage('')

        api.sendMessage(
            thread_id,
            messageToSend,
            assistant?.guid,
            selected?.owner_id || 'schief',
            fileIds
        ).catch(() => {
            setIsLoading(false)
            setLoadingMessageBot(false)
            includeMessageAtArray({
                content: 'Erro ao enviar mensagem. Tente novamente.',
                thread_id: thread_id,
                assistant_id: 'schief',
                author_id: '',
                created_at: new Date(),
                uploaded_documents: [],
            })
        })
    }

    const handleMotorChange = (selectedAssistant: any) => {
        setAssistant(selectedAssistant)
        setIsDropdownOpen(false)
    }

    return (
        <InputWrapper>
            {showAlert && (
                <Alert
                    severity="error"
                    onClose={() => {
                        setShowAlert(false)
                    }}
                >
                    O número máximo de arquivos permitidos por envio é 3.
                </Alert>
            )}
            {showAlertInvalidExtension && (
                <Alert
                    severity="error"
                    onClose={() => {
                        setShowAlertInvalidExtension(false)
                    }}
                >
                    Extensão não permitida. Envie apenas arquivos .pdf ou .docx.
                </Alert>
            )}
            {showAlertFileSize && (
                <Alert
                    severity="error"
                    onClose={() => {
                        setShowAlertFileSize(false)
                    }}
                >
                    O tamanho máximo permitido por arquivo é 20 MB.
                </Alert>
            )}
            {showAlertCredits && (
                <Alert
                    severity="error"
                    onClose={() => {
                        setShowAlertCredits(false)
                    }}
                >
                    Os seus créditos de mensagem acabaram.
                </Alert>
            )}
            {showAlertThreadLimit && (
                <Alert
                    severity="error"
                    onClose={() => {
                        setShowAlertThreadLimit(false)
                    }}
                >
                    Esta conversa já atingiu o limite de{' '}
                    {MAX_FILES_PER_THREAD} arquivos enviados.
                </Alert>
            )}
            {showAlertPageLimit && (
                <Alert
                    severity="error"
                    onClose={() => {
                        setShowAlertPageLimit(false)
                    }}
                >
                    O contexto desta conversa já foi excedido (
                    {MAX_PAGES_PER_THREAD}+ páginas). Inicie uma nova conversa
                    para enviar mais arquivos.
                </Alert>
            )}
            {showAlertFileNeedsText && (
                <Alert
                    severity="error"
                    onClose={() => {
                        setShowAlertFileNeedsText(false)
                    }}
                >
                    Escreva uma mensagem para enviar junto com o arquivo.
                </Alert>
            )}
            {uploadErrorMessage && (
                <Alert
                    severity="error"
                    onClose={() => {
                        setUploadErrorMessage('')
                    }}
                >
                    {uploadErrorMessage}
                </Alert>
            )}

            <List
                style={{
                    maxHeight: '100px',
                    overflowX: 'auto',
                    display: 'flex',
                    flexDirection: 'row',
                }}
            ></List>

            <InputContainer
                onDragOver={(event) => {
                    event.preventDefault()
                }}
                onDrop={(event) => {
                    event.preventDefault()
                    setRemovedFile(false)
                    if (event.dataTransfer.files) {
                        const filesArray = Array.from(event.dataTransfer.files)

                        const allowedExtensions = ['.pdf', '.docx']
                        const validFilesByExtension = filesArray.filter(
                            (file) => {
                                const extension =
                                    '.' +
                                    file.name.split('.').pop()?.toLowerCase()
                                return allowedExtensions.includes(extension)
                            }
                        )

                        const validFiles = validFilesByExtension.filter(
                            (file) => file.size <= MAX_FILE_SIZE_BYTES
                        )

                        if (validFilesByExtension.length < filesArray.length) {
                            setShowAlertInvalidExtension(true)
                        }

                        if (validFiles.length < validFilesByExtension.length) {
                            setShowAlertFileSize(true)
                        }

                        if (
                            selectedFiles.length + validFiles.length >
                            MAX_FILES
                        ) {
                            setShowAlert(true)
                        }

                        const remainingThreadSlots =
                            MAX_FILES_PER_THREAD -
                            totalInThread -
                            selectedFiles.length
                        if (remainingThreadSlots <= 0) {
                            setShowAlertThreadLimit(true)
                        }

                        const pageLimitReached =
                            totalPagesInThread >= MAX_PAGES_PER_THREAD
                        if (pageLimitReached) {
                            setShowAlertPageLimit(true)
                        }

                        const maxNewFiles = Math.max(
                            0,
                            Math.min(
                                MAX_FILES - selectedFiles.length,
                                remainingThreadSlots,
                                pageLimitReached ? 0 : Infinity
                            )
                        )
                        const limitedFiles = validFiles.slice(0, maxNewFiles)
                        setSelectedFiles((prevFiles) => {
                            const combinedFiles = [
                                ...prevFiles,
                                ...limitedFiles,
                            ]
                            return combinedFiles.slice(0, MAX_FILES)
                        })

                        const projectedTotalInThread =
                            totalInThread +
                            selectedFiles.length +
                            limitedFiles.length
                        console.log(
                            `Arquivos na thread: ${projectedTotalInThread}/${MAX_FILES_PER_THREAD} (faltam ${Math.max(
                                0,
                                MAX_FILES_PER_THREAD - projectedTotalInThread
                            )})`
                        )
                    }
                }}
            >
                <Box display="flex" flexDirection="row" flexWrap="wrap" gap={2}>
                    {selectedFiles.map((file, index) => (
                        <Box
                            key={index}
                            display="flex"
                            alignItems="center"
                            borderRadius="4px"
                            padding="8px"
                            maxWidth="250px"
                        >
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                flex={1}
                            >
                                <Box
                                    sx={{
                                        position: 'relative',
                                        width: '46px',
                                        height: '46px',
                                        flexShrink: 0,
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={getFileIcon(file.name)}
                                        alt="Arquivo"
                                        width="46px"
                                        height="46px"
                                    />
                                    {isUploadingFiles && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                inset: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor:
                                                    'rgba(255, 255, 255, 0.65)',
                                                borderRadius: '6px',
                                            }}
                                        >
                                            <CircularProgress size={18} />
                                        </Box>
                                    )}
                                </Box>
                                <ListItemText
                                    style={{maxWidth: '160px'}}
                                    primary={
                                        file.name.length > 25
                                            ? `${file.name.substring(0, 25)}...`
                                            : file.name
                                    }
                                    secondary={`${(file.size / 1024).toFixed(
                                        2
                                    )} KB`}
                                />
                            </Box>
                            <IconButton
                                style={{padding: '0'}}
                                onClick={() => {
                                    setRemovedFile(true)
                                    const newFiles = [...selectedFiles]
                                    newFiles.splice(index, 1)
                                    setSelectedFiles(newFiles)
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    ))}
                </Box>

                <ComposerLayout isExpanded={isTextareaExpanded}>
                    <LeftButtons>
                        <input
                            type="file"
                            ref={fileInputRef}
                            style={{display: 'none'}}
                            accept=".pdf,.docx"
                            onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                            ) => {
                                setRemovedFile(false)
                                if (event.target.files) {
                                    const filesArray = Array.from(
                                        event.target.files
                                    )

                                    const allowedExtensions = ['.pdf', '.docx']
                                    const validFilesByExtension =
                                        filesArray.filter((file) => {
                                            const extension =
                                                '.' +
                                                file.name
                                                    .split('.')
                                                    .pop()
                                                    ?.toLowerCase()
                                            return allowedExtensions.includes(
                                                extension
                                            )
                                        })

                                    const validFiles =
                                        validFilesByExtension.filter(
                                            (file) =>
                                                file.size <= MAX_FILE_SIZE_BYTES
                                        )

                                    if (
                                        validFilesByExtension.length <
                                        filesArray.length
                                    ) {
                                        setShowAlertInvalidExtension(true)
                                    }

                                    if (
                                        validFiles.length <
                                        validFilesByExtension.length
                                    ) {
                                        setShowAlertFileSize(true)
                                    }

                                    if (
                                        selectedFiles.length +
                                            validFiles.length >
                                        MAX_FILES
                                    ) {
                                        setShowAlert(true)
                                    }

                                    const remainingThreadSlots =
                                        MAX_FILES_PER_THREAD -
                                        totalInThread -
                                        selectedFiles.length
                                    if (remainingThreadSlots <= 0) {
                                        setShowAlertThreadLimit(true)
                                    }

                                    const pageLimitReached =
                                        totalPagesInThread >=
                                        MAX_PAGES_PER_THREAD
                                    if (pageLimitReached) {
                                        setShowAlertPageLimit(true)
                                    }

                                    const maxNewFiles = Math.max(
                                        0,
                                        Math.min(
                                            MAX_FILES - selectedFiles.length,
                                            remainingThreadSlots,
                                            pageLimitReached ? 0 : Infinity
                                        )
                                    )
                                    const limitedFiles = validFiles.slice(
                                        0,
                                        maxNewFiles
                                    )
                                    setSelectedFiles((prevFiles) => {
                                        const combinedFiles = [
                                            ...prevFiles,
                                            ...limitedFiles,
                                        ]

                                        return combinedFiles.slice(0, MAX_FILES)
                                    })

                                    const projectedTotalInThread =
                                        totalInThread +
                                        selectedFiles.length +
                                        limitedFiles.length
                                    console.log(
                                        `Arquivos na thread: ${projectedTotalInThread}/${MAX_FILES_PER_THREAD} (faltam ${Math.max(
                                            0,
                                            MAX_FILES_PER_THREAD -
                                                projectedTotalInThread
                                        )})`
                                    )
                                }
                            }}
                            multiple
                        />
                        <FileDropdownWrapper className="motor-dropdown">
                            <AddFileDropdownTrigger
                                type="button"
                                disabled={!loadedFiles}
                                onClick={() => {
                                    setIsDropdownOpen((prev) => !prev)
                                }}
                            >
                                <AddIcon />
                            </AddFileDropdownTrigger>
                            <AddFileDropdownMenu isOpen={isDropdownOpen}>
                                <AddFileDropdownItem
                                    type="button"
                                    disabled={!loadedFiles}
                                    onClick={() => {
                                        if (selectedFiles.length >= MAX_FILES) {
                                            setShowAlert(true)
                                            setIsDropdownOpen(false)
                                            return
                                        }

                                        if (
                                            totalInThread +
                                                selectedFiles.length >=
                                            MAX_FILES_PER_THREAD
                                        ) {
                                            setShowAlertThreadLimit(true)
                                            setIsDropdownOpen(false)
                                            return
                                        }

                                        if (
                                            totalPagesInThread >=
                                            MAX_PAGES_PER_THREAD
                                        ) {
                                            setShowAlertPageLimit(true)
                                            setIsDropdownOpen(false)
                                            return
                                        }

                                        fileInputRef.current?.click()
                                        setIsDropdownOpen(false)
                                    }}
                                >
                                    <FolderOutlinedIcon />
                                    Adicionar arquivo
                                </AddFileDropdownItem>
                            </AddFileDropdownMenu>
                        </FileDropdownWrapper>
                    </LeftButtons>

                    <InputRow>
                        <TextareaContainer>
                            <StyledTextarea
                                ref={inputRef}
                                maxLength={maxLength}
                                placeholder={
                                    window.location.hostname.includes('procon')
                                        ? 'Envie uma mensagem para o schief.ai'
                                        : 'Envie uma mensagem para o Licito.guru'
                                }
                                value={newMessage}
                                onChange={(event) => {
                                    setNewMessage(event.target.value)
                                }}
                                onKeyDown={(event) => {
                                    if (
                                        event.key === 'Enter' &&
                                        !event.shiftKey &&
                                        !isLoading
                                    ) {
                                        event.preventDefault()
                                        handleSendMessage()
                                    }
                                }}
                            />
                        </TextareaContainer>
                    </InputRow>

                    <SendButtonContainer>
                        <SendButton
                            disabled={isLoading}
                            onClick={handleSendMessage}
                        >
                            {isLoading ? (
                                <StopSharpIcon />
                            ) : (
                                <ArrowForwardIcon />
                            )}
                        </SendButton>
                        <StyledChatCounter>
                            <ChatCounter
                                text={newMessage}
                                maxLength={maxLength}
                            />
                        </StyledChatCounter>
                    </SendButtonContainer>
                </ComposerLayout>
            </InputContainer>
        </InputWrapper>
    )
}
